import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HelpWalkthroughStep, WalkthroughRunState } from '../models/help-api.types';
import { HelpWalkthroughAnalyticsNoopService } from './help-walkthrough-analytics.port';

@Injectable({
  providedIn: 'root',
})
export class HelpWalkthroughRunnerService {
  private readonly state$ = new BehaviorSubject<WalkthroughRunState | null>(null);

  constructor(private readonly analytics: HelpWalkthroughAnalyticsNoopService) {}

  get runState$(): Observable<WalkthroughRunState | null> {
    return this.state$.asObservable();
  }

  get snapshot(): WalkthroughRunState | null {
    return this.state$.value;
  }

  start(name: string, steps: HelpWalkthroughStep[]): void {
    if (!steps?.length) {
      return;
    }
    const sorted = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    this.state$.next({ name, steps: sorted, index: 0 });
    const first = sorted[0];
    this.emitAnalytics(s => s.tourStarted(name, first, 0));
  }

  next(): void {
    const s = this.state$.value;
    if (!s) {
      return;
    }
    if (s.index >= s.steps.length - 1) {
      this.finish();
      return;
    }
    const nextIndex = s.index + 1;
    this.state$.next({ ...s, index: nextIndex });
    const step = s.steps[nextIndex];
    this.emitAnalytics(a => a.tourStarted(s.name, step, nextIndex));
  }

  back(): void {
    const s = this.state$.value;
    if (!s || s.index <= 0) {
      return;
    }
    const prevIndex = s.index - 1;
    this.state$.next({ ...s, index: prevIndex });
  }

  exit(): void {
    const s = this.state$.value;
    if (s) {
      this.emitAnalytics(a => a.tourAborted(s.name, s.index));
    }
    this.state$.next(null);
  }

  finish(): void {
    const s = this.state$.value;
    if (s) {
      this.emitAnalytics(a => a.tourFinished(s.name));
    }
    this.state$.next(null);
  }

  private emitAnalytics(fn: (a: HelpWalkthroughAnalyticsNoopService) => void): void {
    fn(this.analytics);
  }
}
